"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebSerialTransport = void 0;
const errors_1 = require("./errors");
const DEFAULT_CONFIG = {
    baudRate: 9600,
    dataBits: 8,
    parity: "none",
    stopBits: 1,
};
/**
 * `SerialTransport` implementation backed by the Web Serial API
 * (`navigator.serial`). Construct with a `SerialPort` obtained from
 * `navigator.serial.requestPort()` — this transport owns the port from
 * `open()` until `close()` and re-applies the active `SerialTransportConfig`
 * on each open.
 *
 * Web Serial doesn't allow reconfiguring an open port, so `configure()`
 * closes and re-opens the port when it has to change baud/parity/etc.
 * mid-session (e.g. K-line baud switching between adapter probe and KWP).
 *
 * The port is supplied externally rather than constructed via
 * `navigator.serial.requestPort()` here so the consumer can run the
 * permission prompt in response to a user gesture (a browser requirement)
 * and persist the granted port via `navigator.serial.getPorts()` across
 * page reloads.
 */
class WebSerialTransport {
    port;
    telegramEndTimeoutMs;
    config = { ...DEFAULT_CONFIG };
    opened = false;
    reader = null;
    writer = null;
    bufferedData = [];
    pendingReads = [];
    readLoop = null;
    closing = false;
    constructor(port, options = {}) {
        this.port = port;
        this.telegramEndTimeoutMs = options.telegramEndTimeoutMs ?? 20;
    }
    async open(_port) {
        if (this.opened)
            return;
        await this.port.open({
            baudRate: this.config.baudRate,
            dataBits: this.config.dataBits,
            parity: this.config.parity,
            stopBits: this.config.stopBits,
        });
        this.opened = true;
        this.attachWriter();
        this.startReadLoop();
    }
    async close() {
        if (!this.opened)
            return;
        this.closing = true;
        // Reject any outstanding reads so callers don't hang on close.
        for (const pending of this.pendingReads.splice(0)) {
            if (pending.timeoutId)
                clearTimeout(pending.timeoutId);
            if (pending.telegramTimeoutId)
                clearTimeout(pending.telegramTimeoutId);
            pending.reject(new Error("Serial port closed"));
        }
        try {
            this.writer?.releaseLock();
        }
        catch {
            /* ignore */
        }
        this.writer = null;
        try {
            await this.reader?.cancel();
        }
        catch {
            /* ignore */
        }
        try {
            this.reader?.releaseLock();
        }
        catch {
            /* ignore */
        }
        this.reader = null;
        // Wait for the read loop to fully unwind so the next open() doesn't race.
        if (this.readLoop) {
            try {
                await this.readLoop;
            }
            catch {
                /* ignore — cancellation surfaces here */
            }
            this.readLoop = null;
        }
        try {
            await this.port.close();
        }
        catch {
            /* ignore */
        }
        this.opened = false;
        this.closing = false;
        this.bufferedData.length = 0;
    }
    async configure(config) {
        const same = this.config.baudRate === config.baudRate &&
            this.config.dataBits === config.dataBits &&
            this.config.parity === config.parity &&
            this.config.stopBits === config.stopBits;
        this.config = { ...config };
        if (!this.opened || same)
            return;
        // Web Serial doesn't allow live reconfiguration. The K+DCAN path
        // legitimately switches baud mid-session (e.g. 115200 for the cable's
        // adapter telegrams, then K-line baud through the adapter); close and
        // re-open with the new parameters.
        await this.close();
        await this.open();
    }
    async setDtr(value) {
        if (!this.opened)
            return;
        await this.port.setSignals({ dataTerminalReady: value });
    }
    async setRts(value) {
        if (!this.opened)
            return;
        await this.port.setSignals({ requestToSend: value });
    }
    async setBreak(durationMs) {
        if (!this.opened)
            return;
        await this.port.setSignals({ break: true });
        await new Promise((resolve) => setTimeout(resolve, Math.max(0, durationMs)));
        await this.port.setSignals({ break: false });
    }
    async purge() {
        this.bufferedData.length = 0;
    }
    async write(data) {
        if (!this.opened || !this.writer) {
            throw new Error("Serial port is not open");
        }
        await this.writer.ready;
        // Web Serial writers accept Uint8Array directly. Clone so the caller can
        // reuse the buffer without us holding a reference past the await.
        await this.writer.write(new Uint8Array(data));
    }
    read(length, timeoutMs) {
        if (length <= 0) {
            return Promise.resolve(new Uint8Array(0));
        }
        return new Promise((resolve, reject) => {
            const pending = {
                length,
                collected: [],
                resolve,
                reject,
            };
            // Drain anything already buffered.
            if (this.bufferedData.length > 0) {
                const take = Math.min(this.bufferedData.length, length);
                pending.collected = this.bufferedData.splice(0, take);
                if (pending.collected.length === length) {
                    resolve(Uint8Array.from(pending.collected));
                    return;
                }
            }
            this.pendingReads.push(pending);
            if (timeoutMs > 0) {
                pending.timeoutId = setTimeout(() => {
                    this.removePending(pending);
                    reject(new errors_1.SerialTimeoutError(`Serial read timed out after ${timeoutMs}ms (got ${pending.collected.length}/${length})`));
                }, timeoutMs);
            }
        });
    }
    attachWriter() {
        if (!this.port.writable) {
            throw new Error("Serial port has no writable stream");
        }
        this.writer = this.port.writable.getWriter();
    }
    startReadLoop() {
        if (!this.port.readable) {
            throw new Error("Serial port has no readable stream");
        }
        const reader = this.port.readable.getReader();
        this.reader = reader;
        this.readLoop = (async () => {
            try {
                while (this.opened) {
                    const { value, done } = await reader.read();
                    if (done)
                        break;
                    if (value && value.length > 0) {
                        this.onIncomingChunk(value);
                    }
                }
            }
            catch (error) {
                if (!this.closing) {
                    // Surface the error to any pending readers so they don't hang.
                    for (const pending of this.pendingReads.splice(0)) {
                        if (pending.timeoutId)
                            clearTimeout(pending.timeoutId);
                        if (pending.telegramTimeoutId)
                            clearTimeout(pending.telegramTimeoutId);
                        pending.reject(error);
                    }
                }
            }
        })();
    }
    onIncomingChunk(chunk) {
        for (let i = 0; i < chunk.length; i++) {
            this.bufferedData.push(chunk[i]);
        }
        this.deliverToPendingReads();
    }
    deliverToPendingReads() {
        while (this.pendingReads.length > 0 && this.bufferedData.length > 0) {
            const pending = this.pendingReads[0];
            const needed = pending.length - pending.collected.length;
            const take = Math.min(needed, this.bufferedData.length);
            const slice = this.bufferedData.splice(0, take);
            pending.collected.push(...slice);
            // Refresh the idle-detection timer each time a byte lands. Once N bytes
            // have arrived, the telegram is treated as complete after
            // telegramEndTimeoutMs of silence — mirrors NodeSerialTransport.
            if (pending.telegramTimeoutId) {
                clearTimeout(pending.telegramTimeoutId);
            }
            if (pending.collected.length === pending.length) {
                this.completePending(pending);
            }
            else if (this.telegramEndTimeoutMs > 0) {
                pending.telegramTimeoutId = setTimeout(() => {
                    // Idle timeout — return whatever we've collected so far so the
                    // caller can decide whether it's enough.
                    this.completePending(pending);
                }, this.telegramEndTimeoutMs);
            }
        }
    }
    completePending(pending) {
        if (pending.timeoutId)
            clearTimeout(pending.timeoutId);
        if (pending.telegramTimeoutId)
            clearTimeout(pending.telegramTimeoutId);
        this.removePending(pending);
        pending.resolve(Uint8Array.from(pending.collected));
    }
    removePending(pending) {
        const idx = this.pendingReads.indexOf(pending);
        if (idx >= 0)
            this.pendingReads.splice(idx, 1);
    }
}
exports.WebSerialTransport = WebSerialTransport;
//# sourceMappingURL=webSerialTransport.js.map